(() => {
  'use strict';

  const CONFIG_SELECTOR = '[data-llcl-variant-media-groups]';
  const PRODUCT_SECTION_SELECTOR = '.shopify-section--main-product';
  const RAIL_SELECTOR = '.product-gallery__carousel';
  const MEDIA_SELECTOR = '.product-gallery__media[data-media-id]';
  const STATE_EVENT = 'llcl:variant-media-state';
  const SHOW_ALL_EVENT = 'llcl:variant-media-show-all';
  const sectionObservers = new WeakMap();

  function variantIdFromUrl() {
    const value = new URL(window.location.href).searchParams.get('variant');
    return value && /^\d+$/.test(value) ? value : null;
  }

  let modeSource = variantIdFromUrl() ? 'navigation' : 'general';
  let showAllOverride = false;

  function normalizeLabel(value) {
    return String(value || '').trim().replace(/:\s*$/, '').toLocaleLowerCase('es');
  }

  function parseConfig(root = document) {
    const node = root?.querySelector?.(CONFIG_SELECTOR) || document.querySelector(CONFIG_SELECTOR);
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      return config && config.groups && config.option_name ? config : null;
    } catch (error) {
      console.warn('[LLCL variant media] Invalid configuration; Prestige fallback preserved.', error);
      return null;
    }
  }

  function findOptionFieldset(section, optionName) {
    const picker = section.querySelector('variant-picker');
    if (!picker) return null;

    const expected = normalizeLabel(optionName);
    return Array.from(picker.querySelectorAll('.variant-picker__option')).find((candidate) => {
      const legend = candidate.querySelector('legend');
      return normalizeLabel(legend?.textContent) === expected;
    }) || null;
  }

  function labelForControl(fieldset, control) {
    if (!control) return null;

    let label = control.closest('label');
    if (!label && control.id) {
      label = fieldset.querySelector(`label[for="${CSS.escape(control.id)}"]`);
    }
    if (!label) return null;

    const conciseLabel = label.querySelector('.sr-only') || label.querySelector('span');
    const value = conciseLabel?.textContent || label.textContent;
    return value?.trim() || null;
  }

  function selectedOptionValue(section, optionName) {
    const fieldset = findOptionFieldset(section, optionName);
    if (!fieldset) return null;

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    const checkedLabel = labelForControl(fieldset, checked);
    if (checkedLabel) return checkedLabel;

    const selectedLabel = fieldset.querySelector('.variant-picker__option-info span');
    if (selectedLabel && selectedLabel.textContent.trim()) return selectedLabel.textContent.trim();

    const dropdownLabel = fieldset.querySelector('.select span[id$="-selected-value"]');
    if (dropdownLabel && dropdownLabel.textContent.trim()) return dropdownLabel.textContent.trim();

    return null;
  }

  function resolveGroup(config, color) {
    if (!color || !config?.groups) return null;
    if (config.groups[color]) return config.groups[color];

    const expected = normalizeLabel(color);
    const key = Object.keys(config.groups).find((candidate) => normalizeLabel(candidate) === expected);
    return key ? config.groups[key] : null;
  }

  function configuredOptionControl(section, optionName, event) {
    if (!(event.target instanceof Element)) return null;

    const option = event.target.closest('.variant-picker__option');
    if (!option || !section.contains(option)) return null;
    if (normalizeLabel(option.querySelector('legend')?.textContent) !== normalizeLabel(optionName)) return null;

    if (event.target.matches('input[type="radio"]')) return event.target;

    const label = event.target.closest('label');
    return label?.control || null;
  }

  function preserveUnitallaDisplay(section) {
    const sizeFieldset = Array.from(section.querySelectorAll('.variant-picker__option')).find((candidate) => {
      const label = normalizeLabel(candidate.querySelector('legend')?.textContent);
      return label === 'tamano' || label === 'talla' || label === 'tamaño';
    });

    if (!sizeFieldset) return;

    const legend = sizeFieldset.querySelector('legend');
    if (legend) legend.textContent = 'Talla:';

    sizeFieldset.querySelectorAll('.block-swatch span, .block-underline span, .variant-picker__option-info span').forEach((node) => {
      if (normalizeLabel(node.textContent) === 'ut') node.textContent = 'Unitalla';
    });
  }

  function mediaNumericId(value) {
    return String(value || '').split('/').pop();
  }

  function currentMode() {
    if (showAllOverride) return 'general';
    return modeSource === 'navigation' || modeSource === 'color' ? 'explicit' : 'general';
  }

  function currentSource() {
    return showAllOverride ? 'show-all' : modeSource;
  }

  function galleryMediaNodes(gallery) {
    const rail = gallery?.querySelector?.(RAIL_SELECTOR);
    if (!rail) return [];
    return Array.from(rail.children).filter((node) => node.matches?.(MEDIA_SELECTOR));
  }

  function syncGalleryControls(gallery, mediaNodes, allowedIds = null) {
    gallery.querySelectorAll('.product-gallery__thumbnail[data-media-id]').forEach((control) => {
      control.hidden = !!allowedIds && !allowedIds.has(mediaNumericId(control.dataset.mediaId));
    });

    const dots = Array.from(gallery.querySelectorAll('carousel-navigation.page-dots .tap-area'));
    dots.forEach((dot, index) => {
      const mediaId = mediaNumericId(mediaNodes[index]?.dataset?.mediaId);
      dot.hidden = !!allowedIds && !!mediaId && !allowedIds.has(mediaId);
    });
  }

  function syncRealGalleryNodes(gallery, allowedIds = null) {
    const nodes = galleryMediaNodes(gallery);

    nodes.forEach((node) => {
      node.hidden = !!allowedIds && !allowedIds.has(mediaNumericId(node.dataset.mediaId));
    });

    syncGalleryControls(gallery, nodes, allowedIds);

    const visibleMediaIds = nodes
      .filter((node) => !node.hidden)
      .map((node) => mediaNumericId(node.dataset.mediaId));

    const expectedVisibleMediaIds = nodes
      .filter((node) => !allowedIds || allowedIds.has(mediaNumericId(node.dataset.mediaId)))
      .map((node) => mediaNumericId(node.dataset.mediaId));

    const domVerified = nodes.length > 0 && nodes.every((node) => {
      const expectedHidden = !!allowedIds && !allowedIds.has(mediaNumericId(node.dataset.mediaId));
      return node.hidden === expectedHidden;
    });

    return {
      totalMedia: nodes.length,
      visibleMediaIds,
      expectedVisibleMediaIds,
      domVerified
    };
  }

  function publishState(section, { color = null, filtered = false, galleryState = null } = {}) {
    const state = {
      mode: currentMode(),
      source: currentSource(),
      variantId: variantIdFromUrl(),
      color,
      filtered,
      gallery: galleryState || null
    };

    section.dataset.llclVariantMediaMode = state.mode;
    section.dataset.llclVariantMediaSource = state.source;
    section.dataset.llclVariantMediaFiltered = filtered ? 'true' : 'false';
    section.dataset.llclVariantMediaDomVerified = galleryState?.domVerified ? 'true' : 'false';
    section.dataset.llclVariantMediaVisibleIds = galleryState?.visibleMediaIds?.join(',') || '';
    document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: state }));
    return state;
  }

  function showAll(gallery) {
    const carousel = gallery?.carousel;
    if (carousel && typeof carousel.filter === 'function') carousel.filter([]);

    const galleryState = syncRealGalleryNodes(gallery, null);
    gallery.setAttribute('filtered-indexes', '[]');
    return galleryState;
  }

  function effectiveAllowedIds(section) {
    if (currentMode() !== 'explicit') return null;

    const config = parseConfig(section);
    const gallery = section.querySelector('product-gallery');
    if (!config || !gallery) return null;

    const color = selectedOptionValue(section, config.option_name);
    const group = resolveGroup(config, color);
    if (!group || !Array.isArray(group.ordered_media_ids) || group.ordered_media_ids.length === 0) return null;

    const allowedIds = new Set(group.ordered_media_ids.map(mediaNumericId));
    const nodes = galleryMediaNodes(gallery);
    if (!nodes.some((node) => allowedIds.has(mediaNumericId(node.dataset.mediaId)))) return null;
    return allowedIds;
  }

  function galleryMatchesExpectedState(section) {
    const gallery = section.querySelector('product-gallery');
    if (!gallery) return true;

    const allowedIds = effectiveAllowedIds(section);
    const nodes = galleryMediaNodes(gallery);
    if (!nodes.length) return true;

    return nodes.every((node) => {
      const expectedHidden = !!allowedIds && !allowedIds.has(mediaNumericId(node.dataset.mediaId));
      return node.hidden === expectedHidden;
    });
  }

  function apply(section) {
    preserveUnitallaDisplay(section);

    const config = parseConfig(section);
    const gallery = section.querySelector('product-gallery');

    if (!config || !gallery) return;

    if (currentMode() !== 'explicit') {
      const galleryState = showAll(gallery);
      publishState(section, { galleryState });
      return;
    }

    const color = selectedOptionValue(section, config.option_name);
    const group = resolveGroup(config, color);

    if (!group || !Array.isArray(group.ordered_media_ids) || group.ordered_media_ids.length === 0) {
      const galleryState = showAll(gallery);
      publishState(section, { color, galleryState });
      return;
    }

    const allowedIds = new Set(group.ordered_media_ids.map(mediaNumericId));
    const realCells = galleryMediaNodes(gallery);
    const filteredIndexes = [];

    realCells.forEach((cell, index) => {
      if (!allowedIds.has(mediaNumericId(cell.dataset.mediaId))) filteredIndexes.push(index);
    });

    if (realCells.length === 0 || filteredIndexes.length === realCells.length) {
      const galleryState = showAll(gallery);
      publishState(section, { color, galleryState });
      return;
    }

    const carousel = gallery.carousel;
    if (carousel && typeof carousel.filter === 'function') {
      carousel.filter(filteredIndexes);
    }

    const galleryState = syncRealGalleryNodes(gallery, allowedIds);
    gallery.setAttribute('filtered-indexes', JSON.stringify(filteredIndexes.map((index) => index + 1)));

    const firstOrderedId = mediaNumericId(group.ordered_media_ids[0]);
    const visibleCells = realCells.filter((cell) => !cell.hidden);
    const targetCell = visibleCells.find((cell) => mediaNumericId(cell.dataset.mediaId) === firstOrderedId) || visibleCells[0];

    publishState(section, { color, filtered: galleryState.domVerified && galleryState.visibleMediaIds.length > 0, galleryState });

    if (!targetCell) return;

    const visibleIndex = visibleCells.indexOf(targetCell);
    if (carousel?.isScrollable && visibleIndex >= 0 && typeof carousel.select === 'function') {
      carousel.select(visibleIndex, { instant: true });
    } else if (!carousel?.isScrollable) {
      targetCell.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }

  function productFormForSection(section) {
    const formId = section.querySelector('product-gallery')?.getAttribute('form');
    return formId ? document.forms[formId] : null;
  }

  function ensureExplicitVariantUrl(section) {
    if (variantIdFromUrl()) return;

    const productForm = productFormForSection(section);
    const variantId = String(productForm?.elements?.id?.value || '').trim();
    if (!/^\d+$/.test(variantId)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }

  function scheduleApply(section) {
    // Prestige can re-render its gallery after the first animation frames.
    // Reconcile the explicit LLCL color selection after that native pass.
    requestAnimationFrame(() => {
      if (!section.isConnected) return;
      apply(section);
      requestAnimationFrame(() => {
        if (section.isConnected) apply(section);
      });
    });
    window.setTimeout(() => {
      if (section.isConnected && !galleryMatchesExpectedState(section)) apply(section);
    }, 120);
    window.setTimeout(() => {
      if (section.isConnected && !galleryMatchesExpectedState(section)) apply(section);
    }, 450);
  }

  function bindGalleryStateGuard(section) {
    if (sectionObservers.has(section)) return;

    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type === 'attributes') {
          return record.attributeName === 'hidden' && record.target instanceof Element && record.target.matches(MEDIA_SELECTOR);
        }

        return Array.from(record.addedNodes || []).some((node) => {
          return node instanceof Element && (node.matches('product-gallery') || node.querySelector?.('product-gallery'));
        });
      });

      if (!relevant || galleryMatchesExpectedState(section)) return;
      scheduleApply(section);
    });

    observer.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden']
    });

    sectionObservers.set(section, observer);
  }

  function enterExplicitColorMode(section, config, event) {
    if (!event.isTrusted) return false;

    const control = configuredOptionControl(section, config.option_name, event);
    if (!control) return false;

    const wasAlreadyChecked = control.checked;
    modeSource = 'color';
    showAllOverride = false;

    requestAnimationFrame(() => {
      if (event.type === 'click' && wasAlreadyChecked) ensureExplicitVariantUrl(section);
      scheduleApply(section);
    });

    return true;
  }

  function productCardVariantUrl(card, variantId) {
    if (!card || !/^\d+$/.test(String(variantId || ''))) return null;

    const anchor = card.querySelector('.product-card__media, .product-title');
    if (!anchor?.href) return null;

    const url = new URL(anchor.href, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function syncProductCardLinks(control) {
    const card = control?.closest?.('product-card');
    const variantId = control?.dataset?.variantId;
    const href = productCardVariantUrl(card, variantId);
    if (!card || !href) return;

    card.querySelectorAll('.product-card__media, .product-title').forEach((link) => {
      link.setAttribute('href', href);
    });

    card.dataset.llclExplicitVariantId = String(variantId);
  }

  function handleProductCardVariantIntent(event) {
    if (!(event.target instanceof Element)) return;

    const control = event.target.matches('product-card input[data-variant-id]')
      ? event.target
      : event.target.closest('label')?.control;

    if (!control?.matches?.('product-card input[data-variant-id]')) return;
    if (event.type === 'click' && !event.isTrusted) return;

    syncProductCardLinks(control);
  }

  function resetFromNavigation() {
    modeSource = variantIdFromUrl() ? 'navigation' : 'general';
    showAllOverride = false;
  }

  function configuredSections() {
    return Array.from(document.querySelectorAll(PRODUCT_SECTION_SELECTOR)).filter((section) => parseConfig(section));
  }

  function showAllMedia() {
    showAllOverride = true;
    configuredSections().forEach(scheduleApply);
  }

  function restoreExplicitMedia() {
    showAllOverride = false;
    configuredSections().forEach(scheduleApply);
  }

  function syncMediaState() {
    configuredSections().forEach(scheduleApply);
  }

  function getPublicState() {
    return {
      mode: currentMode(),
      source: currentSource(),
      variantId: variantIdFromUrl(),
      showAllOverride
    };
  }

  function init(section) {
    if (!section || section.dataset.llclVariantMediaInitialized === 'true') return;

    const config = parseConfig(section);
    if (!config) return;

    section.dataset.llclVariantMediaInitialized = 'true';
    bindGalleryStateGuard(section);

    const productForm = productFormForSection(section);

    section.addEventListener('click', (event) => {
      enterExplicitColorMode(section, config, event);
    }, true);

    section.addEventListener('change', (event) => {
      enterExplicitColorMode(section, config, event);
    }, true);

    if (productForm) {
      productForm.addEventListener('product:rerender', () => scheduleApply(section));
    }

    scheduleApply(section);
  }

  function boot() {
    document.querySelectorAll(PRODUCT_SECTION_SELECTOR).forEach(init);
  }

  window.LLCLVariantMedia = Object.assign(window.LLCLVariantMedia || {}, {
    getState: getPublicState,
    restoreExplicit: restoreExplicitMedia,
    showAll: showAllMedia,
    sync: syncMediaState
  });

  document.addEventListener('click', handleProductCardVariantIntent, true);
  document.addEventListener('change', handleProductCardVariantIntent, true);
  document.addEventListener(SHOW_ALL_EVENT, showAllMedia);
  document.addEventListener('variant:change', () => {
    configuredSections().forEach(scheduleApply);
  });

  window.addEventListener('popstate', () => {
    resetFromNavigation();
    syncMediaState();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    resetFromNavigation();
    syncMediaState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    boot();
    configuredSections().forEach(scheduleApply);
  });

  document.addEventListener('product:rerender', () => {
    requestAnimationFrame(() => {
      boot();
      configuredSections().forEach(scheduleApply);
    });
  });
})();
